import { ReactNode } from 'react';
import { ProgressIndicator, ProgressType } from '../progress-indicator';
import { Composite } from '../../structure/composite';
import { Size, Variant } from '../../types';
import { Button } from '../button';
import {
    Card as StyledCard,
    CardStatic as StyledStaticCard,
    PrimaryContainer,
    ThumbnailContainer,
    TitleContainer,
    SecondaryContent,
} from './card.styles';
import { AlertTriangleIcon } from '../../themes/base/icons/named';

export interface CardProps {
    actions?: ReactNode;
    background?: string;
    children?: ReactNode;
    fillParent?: boolean;
    minHeight?: number;
    name?: string;
    onClick?: () => void;
    secondaryText?: ReactNode;
    thumbnail?: ReactNode;
    tid?: string;
    title?: ReactNode;
    clickable?: boolean;
    size?: Size;
    alignChildren?: string;
    justifyChildren?: string;
    flexDirection?: string;
    loading?: boolean;
    error?: boolean;
    errorButtonText?: string;
    errorButtonOnClick?: () => void;
}

export function Card({
    actions,
    background,
    children,
    fillParent = true,
    minHeight,
    onClick,
    secondaryText,
    tid,
    title,
    thumbnail,
    clickable = true,
    size = Size.NORMAL,
    alignChildren,
    justifyChildren,
    flexDirection,
    loading = false,
    error = false,
    errorButtonText,
    errorButtonOnClick,
}: CardProps) {
    const CardItem: any = clickable ? StyledCard : StyledStaticCard;
    return (
        <CardItem
            data-test-id={tid}
            onClick={onClick}
            size={size}
            $fillParent={fillParent}
            $minHeight={minHeight}
            $background={background}
        >
            <PrimaryContainer>
                <Composite align="flex-start" fillParent={true} noWrap>
                    {thumbnail && <ThumbnailContainer>{thumbnail}</ThumbnailContainer>}
                    {title && (
                        <TitleContainer size={size}>
                            {title}
                            {secondaryText}
                        </TitleContainer>
                    )}
                </Composite>
            </PrimaryContainer>
            <SecondaryContent
                align={alignChildren}
                justify={justifyChildren}
                direction={flexDirection}
            >
                {error ? (
                    <Button
                        icon={AlertTriangleIcon}
                        iconUseMargin
                        onClick={errorButtonOnClick}
                        variant={Variant.TERTIARY}
                        size={Size.TINY}
                        fillParent={false} // This prop should also be transient in Button if needed
                    >
                        {errorButtonText}
                    </Button>
                ) : loading ? (
                    <ProgressIndicator type={ProgressType.DONUT_LOADING} progress={50} />
                ) : (
                    children
                )}
                {actions}
            </SecondaryContent>
        </CardItem>
    );
}
